import { useState, useEffect, useCallback } from "react";
import apiClient from "../utils/apiClient";

/**
 * The institute's enquiry pipeline stages.
 *
 * Replaces the identical hardcoded arrays that had drifted into three screens
 * (`EnquiriesList`, `EnquiryForm`, `EnquiryViewPage`). One fetch, one shared
 * shape — a status dropdown that disagrees with the one on the next page is
 * exactly how a pipeline stops being trusted.
 *
 * The list is cached module-wide because it is small, changes rarely, and is
 * needed by nearly every enquiry screen: refetching it per mount would be a
 * request per navigation for data that is effectively static within a session.
 */

let _cache = null;
let _inFlight = null;

// ⚠️ These are DATA, not styling: each is the default `color` of a pipeline
// stage the admin can edit, and whatever is here gets stored on the stage and
// read back by every screen that renders it. A `hsl(var(--x))` would be written
// to the database and break the day the palette moved, so they stay literals —
// same reasoning as the swatch pickers in PipelineSettings and ContactGroupsTab.
const LEGACY_FALLBACK = [
  { name: "Enquired", color: "#16a34a", semantic: "open", isDefault: true },
  { name: "Confirmed", color: "#2563eb", semantic: "open" },
  { name: "Postponed", color: "#d97706", semantic: "open" },
  { name: "Dropped", color: "#ef4444", semantic: "lost" },
  { name: "Closed", color: "#64748b", semantic: "lost" },
  { name: "Converted", color: "#7c3aed", semantic: "won" },
];

/** Drop the cache after a pipeline edit so every screen picks the change up. */
export function invalidateEnquiryStages() {
  _cache = null;
  _inFlight = null;
}

async function fetchStages() {
  if (_cache) return _cache;
  if (_inFlight) return _inFlight;

  _inFlight = apiClient
    .get("/enquiry-stages")
    .then(({ data }) => {
      _cache = data.stages?.length ? data.stages : LEGACY_FALLBACK;
      return _cache;
    })
    .catch(() => {
      // Never leave a status dropdown empty. A stale-but-sane list beats a
      // screen where the user cannot change a status at all.
      _cache = LEGACY_FALLBACK;
      return _cache;
    })
    .finally(() => { _inFlight = null; });

  return _inFlight;
}

export default function useEnquiryStages() {
  const [stages, setStages] = useState(_cache || LEGACY_FALLBACK);
  const [loading, setLoading] = useState(!_cache);

  const load = useCallback(async () => {
    setLoading(true);
    const result = await fetchStages();
    setStages(result);
    setLoading(false);
  }, []);

  useEffect(() => {
    let cancelled = false;
    fetchStages().then((result) => {
      if (!cancelled) {
        setStages(result);
        setLoading(false);
      }
    });
    return () => { cancelled = true; };
  }, []);

  const stageNames = stages.map((s) => s.name);
  const colorOf = (name) => stages.find((s) => s.name === name)?.color || "#64748b";
  const semanticOf = (name) => stages.find((s) => s.name === name)?.semantic || "open";

  return { stages, stageNames, colorOf, semanticOf, loading, reload: load };
}
